import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { guidFor } from '@ember/object/internals';
import type Owner from '@ember/owner';
import Select from 'tio-ui/components/select';
import FormField from 'tio-ui/components/form-field';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';

type Option = {
  label?: string;
  value?: string;
  [key: string]: string | undefined;
};

interface Signature<T extends Option> {
  Args: {
    label?: string;
    hideLabel?: boolean;
    name?: string;
    onChange?: (value: string) => void;
    optionLabel?: keyof T;
    optionValue?: keyof T;
    options: T[];
    value?: string;
    placeholder?: string;
    selectPlaceholder?: string;
    multiSelect?: boolean;
    errors?: string[];
    hint?: string;
    isDisabled?: boolean;
    isRequired?: boolean;
  };
  Blocks: {
    options: [T];
  };
}

const MAX_HEIGHT = 320;

export default class TioSelectTextArea<T extends Option> extends Component<Signature<T>> {
  @tracked textAreaValue = '';

  private _id = `select-textarea-${guidFor(this)}`;

  get isDisabled(): boolean {
    return this.args.isDisabled === true;
  }

  constructor(owner: Owner, args: Signature<T>['Args']) {
    super(owner, args);
    this.textAreaValue = args.value ?? '';
  }

  get itemsForSelect(): Array<{ key: string; label: string; item: T }> {
    const opts = this.args.options ?? [];
    const { optionLabel, optionValue } = this.args;

    return opts.map((opt) => {
      const label = String(optionLabel ? opt[optionLabel] : opt.label);
      const raw = String(optionValue ? (opt[optionValue] ?? '') : (opt.value ?? ''));
      const key = raw !== '' ? raw : label;
      return { key, label, item: opt };
    });
  }

  get selectedKey(): string {
    // Keep the select in sync when value is set externally
    const { options, optionValue, value } = this.args;
    if (!value) return '';
    const match = options?.find(
      (opt) => String(optionValue ? opt[optionValue] : opt.value) === value
    );
    return match ? String(optionValue ? match[optionValue] : match.value) : '';
  }

  // Keeps the DOM .value property in sync with @value / textAreaValue.
  // Setting the `value=` attribute only sets defaultValue; once a user has typed,
  // the browser's .value property diverges and needs direct assignment.
  syncValueModifier = modifier((el: HTMLTextAreaElement, [val]: [string]) => {
    if (el.value !== val) {
      el.value = val;
    }
    this.applyAutoGrow(el);
  });

  applyAutoGrow = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
  };

  onSelectionChange = (key: string | null) => {
    const { onChange, optionValue, multiSelect } = this.args;

    const selected = key
      ? this.args.options.find((opt) => String(optionValue ? opt[optionValue] : opt.value) === key)
      : undefined;

    const selectedText = selected
      ? String(optionValue ? selected[optionValue] : selected.value)
      : '';

    if (multiSelect && selectedText) {
      const current = this.textAreaValue.trim();
      this.textAreaValue = current ? `${current}\n${selectedText}` : selectedText;
    } else {
      this.textAreaValue = selectedText;
    }

    onChange?.(this.textAreaValue);
  };

  handleTextAreaInput = (event: Event) => {
    const el = event.target as HTMLTextAreaElement;
    this.textAreaValue = el.value;
    this.applyAutoGrow(el);
    this.args.onChange?.(el.value);
  };

  get containerClass(): string {
    const base =
      'rounded-lg border tio-bg-surface transition-colors duration-150 focus-within:outline-none focus-within:ring-2';

    if (this.isDisabled) {
      return `${base} tio-border-default opacity-50 cursor-not-allowed`;
    }

    if (this.args.errors?.length) {
      return `${base} tio-border-danger app-field-error-focus`;
    }

    // `.app-field-focus` owns the focus-within border + ring (canonical
    // input-focus treatment via `--border-focus`, which is `var(--brand)`)
    // so no off-palette violet utilities at the call site.
    return `${base} app-field-focus tio-border-default hover:tio-border-strong`;
  }

  <template>
    <FormField
      @label={{if @hideLabel undefined @label}}
      @hint={{@hint}}
      @errors={{@errors}}
      @isRequired={{@isRequired}}
      @isDisabled={{this.isDisabled}}
      @fieldId={{this._id}}
    >
      <div class={{this.containerClass}}>
        <div class="px-2 pt-2">
          <Select
            @items={{this.itemsForSelect}}
            @selected={{this.selectedKey}}
            @onChange={{this.onSelectionChange}}
            @clearable={{true}}
            @placeholder={{@selectPlaceholder}}
            @size="sm"
            @disabled={{this.isDisabled}}
            data-test-select-textarea
          />
        </div>

        <textarea
          {{on "input" this.handleTextAreaInput}}
          {{this.syncValueModifier this.textAreaValue}}
          placeholder={{@placeholder}}
          disabled={{this.isDisabled}}
          class="block w-full resize-none border-0 bg-transparent py-2 px-3 text-sm tio-fg-primary placeholder:tio-fg-muted ring-0 outline-0 min-h-[5rem]"
          name={{@name}}
          rows="3"
          id={{this._id}}
          data-test-textarea-input
        />
      </div>
    </FormField>
  </template>
}
